import {
    collection,
    query,
    where,
    orderBy,
    startAfter,
    limit,
    getDocs,
    doc,
    addDoc,
    updateDoc,
    deleteDoc,
    serverTimestamp,
  } from "firebase/firestore";
  import { db } from "./firebase"; // Ensure Firebase is properly initialized
  import { usesLocalApi } from "../services/deploymentConfig";
  import { localClinicService } from "../services/clinicLocalApiService";
  
  const CLINICS_COLLECTION = "clinics";
  
  /**
   * Fetch paginated clinics from Firestore.
   * @param {string} hospitalId - The hospital ID.
   * @param {number} pageSize - The number of records per page.
   * @param {object} lastDoc - The last document from the previous page (for pagination).
   * @param {object} filters - Filters to apply on the query (optional).
   * @returns {object} Clinics data including clinics and last visible document.
   */
  export const fetchPaginatedClinics = async (hospitalId, pageSize, lastDoc = null, filters = {}) => {
    if (usesLocalApi()) {
      return localClinicService.fetchPaginatedClinics(hospitalId, pageSize, lastDoc, filters);
    }

    try {
      if (!hospitalId) {
        throw new Error("Hospital ID is required.");
      }
  
      const clinicsRef = collection(db, CLINICS_COLLECTION);
  
      // Build query constraints
      const queryConstraints = [where("hospitalId", "==", hospitalId)];
      if (filters.name) {
        queryConstraints.push(where("name", "==", filters.name));
      }
      if (filters.location) {
        queryConstraints.push(where("location", "==", filters.location));
      }
  
      queryConstraints.push(orderBy("createdAt", "desc")); // Ensure consistent ordering
      queryConstraints.push(limit(pageSize));
  
      if (lastDoc) {
        queryConstraints.push(startAfter(lastDoc));
      }
  
      // Run the query
      const q = query(clinicsRef, ...queryConstraints);
      const querySnapshot = await getDocs(q);
  
      const clinics = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
  
      const lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1] || null;
  
      return {
        clinics,
        lastVisible,
      };
    } catch (error) {
      console.error("Error fetching paginated clinics:", error.message);
      throw new Error("Failed to fetch paginated clinics: " + error.message);
    }
  };

  

/**
 * Fetch all clinics for a specific hospital without pagination, sorted by name ASC.
 * @param {string} hospitalId - The hospital ID.
 * @returns {Array} Array of clinics.
 */
export const fetchClinics = async (hospitalId) => {
  if (usesLocalApi()) {
    return localClinicService.fetchClinics(hospitalId);
  }

  try {
    if (!hospitalId) {
      throw new Error("Hospital ID is required.");
    }

    const clinicsRef = collection(db, CLINICS_COLLECTION);
    const clinicsQuery = query(
      clinicsRef,
      where("hospitalId", "==", hospitalId),
      orderBy("name", "asc")
    );

    const querySnapshot = await getDocs(clinicsQuery);

    const clinics = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    return clinics;
  } catch (error) {
    console.error("Error fetching clinics:", error.message);
    throw new Error("Failed to fetch clinics: " + error.message);
  }
};


  
  /**
   * Add a new clinic to Firestore.
   * @param {string} hospitalId - The hospital ID.
   * @param {object} clinicData - The clinic data to save.
   * @returns {object} Response with success or error message.
   */
  export const addClinic = async (hospitalId, clinicData,user) => {
    if (usesLocalApi()) {
      return localClinicService.addClinic(hospitalId, clinicData, user);
    }

    try {
      if (!hospitalId) {
        throw new Error("Hospital ID is required.");
      }
  
      const clinicsRef = collection(db, CLINICS_COLLECTION);
      const data = {
        ...clinicData,
        hospitalId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        user
      };
      const docRef = await addDoc(clinicsRef, data);
      return { success: true, message: "Clinic added successfully.", id: docRef.id };
    } catch (error) {
      console.error("Error adding clinic:", error.message);
      throw new Error("Failed to add clinic: " + error.message);
    }
  };
  
  /**
   * Update an existing clinic in Firestore.
   * @param {string} hospitalId - The hospital ID.
   * @param {string} clinicId - The ID of the clinic to update.
   * @param {object} clinicData - The updated clinic data.
   * @returns {object} Response with success or error message.
   */
  export const updateClinic = async (hospitalId, clinicId, clinicData) => {
    if (usesLocalApi()) {
      return localClinicService.updateClinic(hospitalId, clinicId, clinicData);
    }

    try {
      if (!hospitalId) {
        throw new Error("Hospital ID is required.");
      }
  
      const clinicRef = doc(db, CLINICS_COLLECTION, clinicId);
      const data = {
        ...clinicData,
        updatedAt: serverTimestamp(),
      };
      await updateDoc(clinicRef, data);
      return { success: true, message: "Clinic updated successfully." };
    } catch (error) {
      console.error("Error updating clinic:", error.message);
      throw new Error("Failed to update clinic: " + error.message);
    }
  };

  export const deleteClinic = async (hospitalId, clinicId) => {
    if (usesLocalApi()) {
      return localClinicService.deleteClinic(hospitalId, clinicId);
    }

    try {
      if (!hospitalId || !clinicId) {
        throw new Error("Hospital ID and clinic ID are required.");
      }

      await deleteDoc(doc(db, CLINICS_COLLECTION, clinicId));
      return { success: true, message: "Clinic deleted successfully." };
    } catch (error) {
      console.error("Error deleting clinic:", error.message);
      throw new Error("Failed to delete clinic: " + error.message);
    }
  };
  
